import React from 'react';
import I18n from 'i18n-js';

import FormGroupWithInput from '@reverbdotcom/commons/src/components/form_group_with_input';

import { core_apimessages_ShippingLabelPackage } from '@reverbdotcom/commons/src/gql/graphql';
import {
  convertPoundsAndOuncesToPounds, convertPoundsToPoundsAndOunces, OZ_TO_LB, WeightUnit,
} from './shipping_label_package';
import { PackageAttribute } from './custom_package_inputs';

interface IProps {
  inputName: string;
  fieldName: string;
  titleKey: string;
  subtitleKey?: string;
  measurementUnit: string;
  value: string;

  onUpdate(attributes: core_apimessages_ShippingLabelPackage);

  shippingPackage: core_apimessages_ShippingLabelPackage;
  roundUpUserInput?: boolean;
  disabled?: boolean;
}

// this really just exposes weight as a combined pounds/ounces input
// but in reality we're just converting such inputs to its decimal pounds equivalent
// so backend stays the same.
function PoundsOuncesInputPair({ weightInPounds = 0, onUpdate, disabled }) {
  const { pounds: currentPounds = 0, ounces: currentOunces = 0 } = convertPoundsToPoundsAndOunces(weightInPounds);

  function onPoundsUpdate(incoming) {
    const newPoundsValue = Number(incoming);
    if (newPoundsValue >= 0) {
      onUpdate({ weight: convertPoundsAndOuncesToPounds(newPoundsValue, currentOunces).toString() });
    }
  }

  function onOuncesUpdate(incoming) {
    const newOuncesValue = Number(incoming);
    if (newOuncesValue >= 0 && newOuncesValue < OZ_TO_LB) {
      onUpdate({ weight: convertPoundsAndOuncesToPounds(currentPounds, newOuncesValue).toString() });
    }
  }

  return (
    <div className="mb-4 d-flex fx-justify-between gap-4 fx-align-end">
      <div className="width-50">
        <FormGroupWithInput
          label={I18n.t('discovery.dashboard.selling.shippingLabels.package.weightTitle')}
          numberOnly
          tagRequired
          value={String(currentPounds || '')}
          inputName="product[shipping][weight][pounds]"
          fieldName="pounds"
          updateField={({ pounds: inputtedPoundsString }) => onPoundsUpdate(inputtedPoundsString)}
          currencySuffix={WeightUnit.POUNDS}
          disabled={disabled}
          inputType="number"
          step="1"
          inputMode="numeric"
        />
      </div>
      <div className="width-50">
        <FormGroupWithInput
          label=""
          numberOnly
          required={currentPounds === 0 && currentOunces === 0}
          value={String(currentOunces || '')}
          inputName="product[shipping][weight][ounces]"
          fieldName="ounces"
          updateField={({ ounces: inputtedOuncesString }) => onOuncesUpdate(inputtedOuncesString)}
          currencySuffix={WeightUnit.OUNCES}
          disabled={disabled}
          inputType="number"
          step="1"
          inputMode="numeric"
        />
      </div>
    </div>
  );
}

export default function PackageInput({
  fieldName,
  measurementUnit,
  shippingPackage,
  titleKey,
  value,
  inputName,
  onUpdate,
  subtitleKey = null,
  roundUpUserInput = false,
  disabled = false,
}: IProps) {

  function buildPackageDimensionAttributes(inputAttributes): Partial<core_apimessages_ShippingLabelPackage> {
    const dimensionAttributes = {
      value: inputAttributes[fieldName],
      unit: measurementUnit,
    };
    const packageAttributes = {};
    packageAttributes[fieldName] = dimensionAttributes;
    return packageAttributes;
  }

  function updateShippingPackageDimensions(attributes) {
    const packageAttributes = buildPackageDimensionAttributes(attributes);

    onUpdate(
      {
        ...shippingPackage,
        ...packageAttributes,
      },
    );
  }

  function roundValue(event) {
    const targetValue = event.target.value;
    const attributes = { [fieldName]: Math.ceil(targetValue).toString() };

    updateShippingPackageDimensions(attributes);
  }

  if (fieldName === PackageAttribute.WEIGHT && measurementUnit === WeightUnit.POUNDS) {
    return (
      <PoundsOuncesInputPair weightInPounds={Number(value)} onUpdate={updateShippingPackageDimensions}
        disabled={disabled} />
    );
  } else {
    return (
      <FormGroupWithInput
        onBlur={roundUpUserInput ? roundValue : null}
        label={I18n.t(`discovery.dashboard.selling.shippingLabels.package.${titleKey}`)}
        helpText={subtitleKey && I18n.t(`discovery.dashboard.selling.shippingLabels.package.${subtitleKey}`)}
        numberOnly
        required
        tagRequired
        value={value}
        inputName={inputName}
        fieldName={fieldName}
        updateField={updateShippingPackageDimensions}
        currencySuffix={measurementUnit}
        disabled={disabled}
        inputMode="decimal"
      />
    );
  }
}
